/**
 * RDKit.js Loader
 * 
 * Phase 4: Singleton promise-based loader for RDKit.js WASM module
 * 
 * Usage:
 *   import { loadRDKit } from './rdkitLoader';
 *   const RDKit = await loadRDKit();
 *   const mol = RDKit.get_mol('CCO'); // ethanol SMILES
 */

let rdkitPromise: Promise<any> | null = null;
let rdkitModule: any = null;

/**
 * Load RDKit.js script dynamically
 */
function loadRDKitScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (typeof window !== 'undefined' && (window as any).initRDKitModule) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[data-rdkit-loader]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    // Create script tag to load RDKit from public directory
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = '/RDKit_minimal.js';
    script.setAttribute('data-rdkit-loader', 'true');
    
    script.onload = () => {
      if (typeof (window as any).initRDKitModule === 'function') {
        resolve();
      } else {
        reject(new Error('initRDKitModule not found after script load'));
      }
    };
    
    script.onerror = () => {
      reject(new Error('Failed to load RDKit script'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * Load RDKit.js WASM module
 * Returns a promise that resolves to the RDKit module
 */
export async function loadRDKit(): Promise<any> {
  // Return cached module if already loaded
  if (rdkitModule) {
    return Promise.resolve(rdkitModule);
  }

  // Return existing promise if already loading
  if (rdkitPromise) {
    return rdkitPromise;
  }

  // Start loading
  rdkitPromise = (async () => {
    try {
      // Load RDKit script first
      await loadRDKitScript();
      
      // Get initRDKitModule from window
      const initRDKitModule = (window as any).initRDKitModule;
      
      if (!initRDKitModule || typeof initRDKitModule !== 'function') {
        throw new Error('initRDKitModule function not found on window object');
      }
      
      // Initialize RDKit with locateFile to handle WASM path correctly
      // WASM file is copied to public directory for Vite to serve correctly
      const module = await initRDKitModule({
        locateFile: (file: string) => {
          if (file.endsWith('.wasm')) {
            // Use WASM file from public directory (served by Vite with correct MIME type)
            return `/RDKit_minimal.wasm`;
          }
          return file;
        },
      });
      
      // Cache the module
      rdkitModule = module;
      
      return module;
    } catch (error) {
      // Reset promise on error so we can retry
      rdkitPromise = null;
      console.error('Failed to load RDKit:', error);
      throw new Error(`Failed to load RDKit.js: ${error}`);
    }
  })();

  return rdkitPromise;
}

/**
 * Check if RDKit is already loaded
 */
export function isRDKitLoaded(): boolean {
  return rdkitModule !== null;
}

/**
 * Get the cached RDKit module (returns null if not loaded)
 */
export function getRDKitModule(): any {
  return rdkitModule;
}

